import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {FormBuilder, FormGroup} from '@angular/forms';
import {from, Subject} from 'rxjs';
import {groupBy, mergeMap, reduce, takeUntil, toArray} from 'rxjs/operators';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import {MatDialog} from '@angular/material/dialog';
import {MatTableDataSource} from '@angular/material/table';
import {ActivityService} from '../../../../../services/activity.service';
import {ActivityDisplayDTO} from '../../../../../models/utilisation/pha/ActivityDisplayDTO';
import {CaseHospitalDTO} from '../../../../../models/case-hospital/CaseHospitalDTO';
import {DateUtil} from '../../../../../../common/util/DateUtil';
import {EntRequestBuilder} from '../../../../../../ent/services/ent-request-builder';
import {LookupValue} from '../../../../../types/lookup-value.enum';
import {SharedService} from '../../../../../../common/services/shared-service';
import {TableControl} from '../../../../../../common/models/TableControl';
import {
  GenericListDialogComponent
} from 'src/app/modules/common/dialogs/generic-list-dialog/generic-list-dialog.component';
import {formatNumber} from '../../../../../../common/util/Util';
import {PagedResponseDTO} from '../../../../../../common/models/PagedResponseDTO';
import {RequestBuilderFunctions} from '../../../../../../common/functions/request-builder.functions';
import {detailsExpand} from '../../../../../../common';

enum TableColumns {
  ActivityId = 'activityId',
  ActivityStartDate = 'activityStartDate',
  Type = 'type',
  Provider = 'provider',
  TariffCode = 'tariffCode',
  BenefitFamily = 'benefitFamily',
  Benefit = 'benefit', // THIS IS DUMMY COLUMN THAT SHOWS sums the "benefitCode" + "benefitName"
  Quantity = 'requestedQuantity',
  AmountClaimed = 'amountClaimed',
  AmountBenefit = 'amountBenefit',
  TotalAmount = 'totalAmount', // THIS IS DUMMY COLUMN THAT SHOWS sums the "APPROVED AMT" and "BENEFIT AMT" values
  AdjudicationStatus = 'adjudicationStatus',
}

@Component({
  selector: 'app-case-utilisaton-step',
  templateUrl: './case-utilisaton-step.component.html',
  styleUrls: [
    '../../../../../../../../styles/global/_add-view-edit-styles.scss',
    '../../../../../../../../styles/global/_management-styles.scss'
  ],
  animations: [detailsExpand]
})
export class CaseUtilisatonStepComponent implements OnChanges, OnInit, OnDestroy {
  readonly destroyed$ = new Subject();
  readonly lookupValue = LookupValue;
  readonly dateFormat = DateUtil.dateFormat;
  readonly tableColumns = TableColumns;

  displayedColumns: string[] = Object.values(TableColumns);
  tableControl: TableControl;
  dataSource: MatTableDataSource<ActivityDisplayDTO> = new MatTableDataSource();
  utilisationExpandedRow: ActivityDisplayDTO | null;

  @Input() data: CaseHospitalDTO;
  @Input() formGroup: FormGroup;
  @Input() isEditMode: boolean;
  @Output() updateForm: EventEmitter<FormGroup> = new EventEmitter();
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild('utilisationPaginator', {static: false}) utilisationPaginator: MatPaginator;

  constructor(private activityService: ActivityService,
    private dialog: MatDialog,
    private sharedService: SharedService,
    private formBuilder: FormBuilder) {
    const controls = EntRequestBuilder.getTableControls();
    this.tableControl = new TableControl(
      controls.length,
      10000,
      controls.pageNumber,
      controls.pageSizeOptions,
      'startDate',
      'DESCENDING',
      '',
      ''
    );
  }

  @ViewChild('MatSortUtilisationStep') set matSort(ms: MatSort) {
    if (!ms) {
      return;
    }
    this.dataSource.sortingDataAccessor = (item: ActivityDisplayDTO, property: string) => {
      switch (property) {
      case TableColumns.Benefit:
        // Note:
        //  Column shows sums the "benefitCode" + "benefitName"
        //  So currently filter this column by benefitCode.
        return item['benefitCode'];
      case TableColumns.AdjudicationStatus:
      case TableColumns.Provider:
        return item[property].name;
      case property:
        return item[property];
      default:
        return null;
      }
    };
    this.dataSource.sort = ms;
  }

  get isDataExist(): boolean {
    return this.dataSource && (this.dataSource.data && this.dataSource.data.length > 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isEditMode) {
      this.isEditMode = changes.isEditMode.currentValue;
    }
    if (changes.formGroup) {
      this.formGroup = changes.formGroup.currentValue;
    }
    if (changes.data && changes.data.currentValue) {
      this.data = changes.data.currentValue;
      if (this.isEditMode) {
        this.getListOfActivities(this.tableControl);
      }
    }
  }

  ngOnInit(): void {
    this.initUtilisationStepFormControls();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  initUtilisationStepFormControls(): void {
    this.formGroup = this.formBuilder.group({});

    // Notify Updated Reference of FormGroup to parent:
    this.updateForm.next(this.formGroup);
  }

  pageChanged($event: PageEvent): void {
    this.tableControl.pageNumber = $event.pageIndex;
    this.tableControl.pageSize = $event.pageSize;
    this.getListOfActivities(this.tableControl);
  }

  getListOfActivities(tableControl: TableControl): void {
    const policyId = this.data.caseDTO?.policy?.policyId;
    if (!policyId) {
      return;
    }
    const pagedRequestDTO = RequestBuilderFunctions.getTblActivityPagedRequestDTO(tableControl, String(policyId));
    this.activityService.getListOfActivities(pagedRequestDTO)
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe({
        next:  (response: PagedResponseDTO<ActivityDisplayDTO>) => {
          this.dataSource.data = response.resultList;
          this.tableControl.length = response.totalRecords;
        },
        error: (response: PagedResponseDTO<ActivityDisplayDTO>) => {
          this.dataSource.data = response.resultList;
          this.tableControl.length = response.totalRecords;
        }
      });
  }

  showTotalPerBenefitsFamily(): void {
    const COLUMNS_NAMES = 'Benefit Family;Total';
    const columns: string[] = [COLUMNS_NAMES];
    const data = from(this.dataSource.data);

    data.pipe(
      groupBy(person => person.benefitFamily),
      mergeMap(group => group
        .pipe(
          reduce((acc, cur) => {
            acc.push(cur);
            return acc;
          }, [])
        )
      ),
      toArray()
    )
      .subscribe((groupByBenefitFamilyItems: any[]) => {
        groupByBenefitFamilyItems.forEach(item => {
          const displayStr = this.createDisplayStringGenericListDialog(item);
          columns.push(displayStr);
        });
      });

    this.dialog.open(GenericListDialogComponent, {
      width: '40vw',
      maxHeight: '80vh',
      data: {list: columns, heading: 'Utilisation Per Benefit Family'}
    });
  }

  private createDisplayStringGenericListDialog(groupByItems: ActivityDisplayDTO[]): string {
    if (groupByItems && groupByItems.length > 0) {
      let sum = 0;
      groupByItems.map(item => sum += item.amountBenefit);

      // All items in list will have same names -> choosen from top item for display
      return `${groupByItems[0].benefitFamily};${formatNumber(sum)}`;
    }
    else {
      return 'No details available;';
    }
  }

}
